"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import Link from "next/link";
import ChevronDown from "@repo/icons/react/ChevronDown";
import { cn } from "@/lib/utils";

type ResourceLink = {
  label: string;
  href: string;
  external?: boolean;
  premium?: boolean;
};

type ResourceGroup = {
  title: string;
  links: ResourceLink[];
};

const RESOURCE_GROUPS: ResourceGroup[] = [
  {
    title: "Tools",
    links: [
      {
        label: "Businesses for Sale Near Me",
        href: "/businesses-for-sale-near-me",
      },
      { label: "Free Business Valuation", href: "/free-business-valuation" },
      { label: "Tools for Sellers", href: "/free-tools/sellers" },
      { label: "Tools for Buyers", href: "/free-tools/buyers" },
    ],
  },
  {
    title: "Learn",
    links: [
      { label: "Blog", href: "/blog" },
      {
        label: "Help Center",
        href: "https://support.venturu.com",
        external: true,
      },
      { label: "Business Glossary", href: "/dictionary" },
    ],
  },
  {
    title: "For Brokers",
    links: [
      { label: "Broker Home", href: "/for-brokers" },
      { label: "Venturu One", href: "/for-brokers/venturu-one", premium: true },
      { label: "Advertise", href: "/advertise" },
    ],
  },
  {
    title: "Developers",
    links: [
      {
        label: "ChatGPT App",
        href: "/chatgpt",
      },
      {
        label: "API Documentation",
        href: "https://developers.venturu.com",
        external: true,
      },
    ],
  },
];

function isPathActive(pathname: string, href: string) {
  const hrefPath = href.split("?")[0] ?? href;
  if (hrefPath === "/") {
    return pathname === "/" || pathname.startsWith("/businesses");
  }
  return pathname === hrefPath || pathname.startsWith(`${hrefPath}/`);
}

export function MainNav({ isAgent }: { isAgent: boolean }) {
  const t = useTranslations();
  const pathname = usePathname();
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const navItems = useMemo(
    () =>
      isAgent
        ? [{ href: "/businesses?intent=scout", label: "Scout for Clients" }]
        : [
            { href: "/", label: t("web.topNav.buy") },
            { href: "/sell-your-business", label: t("web.topNav.sell") },
            { href: "/business-brokers", label: t("web.topNav.findBroker") },
          ],
    [isAgent, t],
  );

  function openResources() {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setResourcesOpen(true);
  }

  function closeResourcesWithDelay() {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      setResourcesOpen(false);
      closeTimerRef.current = null;
    }, 100);
  }

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setResourcesOpen(false);
  }, [pathname]);

  return (
    <nav
      className="hidden lg:flex items-center gap-8"
      aria-label={t("web.a11y.mainNav")}
    >
      {navItems.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          className={cn(
            "whitespace-nowrap text-[15px] font-medium text-muted-foreground transition-colors hover:text-foreground",
            isPathActive(pathname, item.href) &&
              "font-semibold text-foreground",
          )}
        >
          {item.label}
        </Link>
      ))}

      <div
        className="relative"
        onMouseEnter={openResources}
        onMouseLeave={closeResourcesWithDelay}
      >
        <button
          type="button"
          onClick={() => setResourcesOpen((prev) => !prev)}
          className={cn(
            "inline-flex items-center gap-1 text-[15px] font-medium text-muted-foreground transition-colors hover:text-foreground",
            resourcesOpen && "text-foreground",
          )}
          aria-expanded={resourcesOpen}
          aria-haspopup="true"
          aria-label={t("web.a11y.resourcesMenu")}
        >
          Resources
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              resourcesOpen && "rotate-180",
            )}
          />
        </button>

        {resourcesOpen ? (
          <div
            className="absolute left-0 top-full z-[1010] pt-3"
            onMouseEnter={openResources}
            onMouseLeave={closeResourcesWithDelay}
          >
            <div className="w-[760px] rounded-xl border border-border bg-popover p-6 text-popover-foreground shadow-[0_8px_30px_rgba(0,0,0,0.08)] animate-in fade-in slide-in-from-top-1 duration-150 dark:shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
              <div className="grid grid-cols-4 gap-8">
                {RESOURCE_GROUPS.map((group) => (
                  <div key={group.title}>
                    <p className="text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                      {group.title}
                    </p>
                    <div className="mt-3 flex flex-col gap-2.5">
                      {group.links.map((link) => (
                        <a
                          key={link.label}
                          href={link.href}
                          target={link.external ? "_blank" : undefined}
                          rel={link.external ? "noreferrer" : undefined}
                          className="inline-flex items-center gap-2 text-[15px] font-normal text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {link.label}
                          {link.premium ? (
                            <span className="inline-flex h-2 w-2 rounded-full bg-success" />
                          ) : null}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </nav>
  );
}
